import React, { useState } from "react";

import {
  Container,
  Button,
  Group,
  Paper,
  Text,
  Avatar,
  Tabs,
  Grid,
  Card,
  Badge,
  ActionIcon,
  TextInput,
  Select,
  Stack,
  Divider,
  Tooltip,
  Modal,
  Pagination,
  Loader,
  Progress,
  NumberFormatter,
  ThemeIcon,
  Drawer,
  Switch,
  MultiSelect,
  RingProgress,
  SimpleGrid,
  Skeleton,
  Timeline,
  Accordion,
  Alert,
  Notification,
  Blockquote,
  Mark,
  Code,
  Chip,
  SegmentedControl,
} from "@mantine/core";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";
import { NonPaginatedResponse, PaginatedResponse } from "../../Lib/responses";
import {
  PaginationProps,
  PlanProps,
  StudentProfileProps,
} from "../../Lib/types";
import { Endpoints } from "../../Lib/endpoints";
import { useDisclosure } from "@mantine/hooks";
import { useDebouncedValue } from "@mantine/hooks";

import "./styles.scss";
import {
  IconUser,
  IconUsers,
  IconSearch,
  IconFilter,
  IconEye,
  IconEdit,
  IconTrash,
  IconMail,
  IconPhone,
  IconCalendar,
  IconChartBar,
  IconUserCheck,
  IconUserX,
  IconSettings,
  IconDownload,
  IconUpload,
  IconStar,
  IconHeart,
  IconMessage,
  IconBell,
  IconShield,
  IconBook,
  IconTrophy,
  IconClock,
  IconMapPin,
  IconWorld,
  IconTrendingUp,
  IconActivity,
  IconTarget,
  IconBrandGoogle,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconInfoCircle,
  IconAlertTriangle,
  IconRefresh,
  IconFileExport,
  IconPlus,
  IconLogin2,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useForm } from "@mantine/form";
import { Colors, HttpSuccessCodesObj } from "../../Lib/data";
import {
  DefaultSuccessNotification,
  DefaultErrorNotification,
  getCurrencySymbol,
} from "../../Lib/methods";
import { Link } from "react-router-dom";

dayjs.extend(relativeTime);

const Students: React.FC = () => {
  const [pagination, setPagination] = useState<PaginationProps>({
    page: 1,
    limit: 12,
  });

  const {
    data: students,
    isLoading: isLoadingStudents,
    reloadData: reloadStudents,
  } = usePerformRequest<PaginatedResponse<StudentProfileProps[]>>({
    method: Endpoints.GetStudents(pagination.page, pagination.limit).method,
    route: Endpoints.GetStudents(pagination.page, pagination.limit).route,
  });

  const {
    data: stats,
    isLoading: isLoadingStats,
    reloadData: reloadStats,
  } = usePerformRequest<
    NonPaginatedResponse<{
      numberOfStudents: number;
      numberOfActiveStudents: number;
      numberOfVerifiedStudents: number;
      numberOfSuspendedStudents: number;
    }>
  >({
    method: Endpoints.GetStudentsStats().method,
    route: Endpoints.GetStudentsStats().route,
  });

  const { data: plans } = usePerformRequest<PaginatedResponse<PlanProps[]>>({
    method: Endpoints.GetPlans(1, 200).method,
    route: Endpoints.GetPlans(1, 200).route,
    body: {},
  });

  const [selectedStudent, setSelectedStudent] =
    useState<StudentProfileProps | null>(null);
  const [
    isDetailModalOpened,
    { open: openDetailModal, close: closeDetailModal },
  ] = useDisclosure(false);
  const [
    isUpdateModalOpened,
    { open: openUpdateModal, close: closeUpdateModal },
  ] = useDisclosure(false);
  const [isUpdatingStudent, setIsUpdatingStudent] = useState(false);
  const [
    isEnrollPlanModalOpened,
    { open: openEnrollPlanModal, close: closeEnrollPlanModal },
  ] = useDisclosure(false);
  const [isEnrollingStudentInPlan, setIsEnrollingStudentInPlan] =
    useState(false);
  const [
    isRemovePlanModalOpened,
    { open: openRemovePlanModal, close: closeRemovePlanModal },
  ] = useDisclosure(false);
  const [removePlanID, setRemovePlanID] = useState("");
  const [isRemovingStudentFromPlan, setIsRemovingStudentFromPlan] =
    useState(false);
  const [
    isDeleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);
  const [isDeletingStudent, setIsDeletingStudent] = useState(false);
  const [
    isSuspensionModalOpened,
    { open: openSuspensionModal, close: closeSuspensionModal },
  ] = useDisclosure(false);
  const [isTogglingSuspension, setIsTogglingSuspension] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const studentAppBaseUrl = process.env.REACT_APP_STUDENT_BASE_URL || "https://emrilacademy.com";

  const handleStudentClick = (student: StudentProfileProps) => {
    setSelectedStudent(student);
    openDetailModal();
  };

  const handleEditClick = (
    student: StudentProfileProps,
    event?: React.MouseEvent
  ) => {
    if (event) {
      event.stopPropagation(); // Prevent card click
    }
    setSelectedStudent(student);
    updateForm.setValues({
      name: student.name,
      email: student.email,
    });
    openUpdateModal();
  };

  const handleDeleteClick = (
    student: StudentProfileProps,
    event?: React.MouseEvent
  ) => {
    if (event) {
      event.stopPropagation(); // Prevent card click
    }
    setSelectedStudent(student);
    openDeleteModal();
  };

  const handleSuspensionClick = (
    student: StudentProfileProps,
    event?: React.MouseEvent
  ) => {
    if (event) {
      event.stopPropagation(); // Prevent card click
    }
    setSelectedStudent(student);
    openSuspensionModal();
  };

  const handleSimulateStudentLogin = async (
    student: StudentProfileProps,
    event?: React.MouseEvent
  ) => {
    if (event) {
      event.stopPropagation();
    }

    const r = await PerformRequest<
      NonPaginatedResponse<{ token: string; student: StudentProfileProps }>
    >({
      method: Endpoints.GetSimulateStudentLogin(student.id).method,
      route: Endpoints.GetSimulateStudentLogin(student.id).route,
    });

    if (r && r.status === HttpSuccessCodesObj.Success && r.data?.data?.token) {
      const targetUrl = `${studentAppBaseUrl}/login?token=${encodeURIComponent(
        r.data.data.token
      )}&redirect=${encodeURIComponent("/d/overview")}`;
      window.open(targetUrl, "_blank", "noopener,noreferrer");
      DefaultSuccessNotification(`Opened ${student.name} in the student dashboard`);
    } else {
      DefaultErrorNotification(r?.message || "Failed to simulate student login");
    }
  };

  const enrollPlanForm = useForm({
    initialValues: {
      planID: "",
    },
    validate: {
      planID: (value) => (value ? null : "Please select a plan"),
    },
  });

  const handleEnrollPlanClick = (
    student: StudentProfileProps,
    event?: React.MouseEvent
  ) => {
    if (event) {
      event.stopPropagation();
    }
    setSelectedStudent(student);
    enrollPlanForm.reset();
    openEnrollPlanModal();
  };

  const handleEnrollStudentInPlan = async () => {
    const { hasErrors } = enrollPlanForm.validate();
    if (hasErrors || !selectedStudent) return;

    setIsEnrollingStudentInPlan(true);
    const r = await PerformRequest({
      method: Endpoints.GetEnrollStudentInPlan(selectedStudent.id).method,
      route: Endpoints.GetEnrollStudentInPlan(selectedStudent.id).route,
      data: { planID: enrollPlanForm.values.planID },
      callback: () => {
        setIsEnrollingStudentInPlan(false);
      },
    });
    setIsEnrollingStudentInPlan(false);

    if (r && r.status === HttpSuccessCodesObj.Success) {
      DefaultSuccessNotification("Student added to plan successfully");
      setSelectedStudent((current) =>
        current
          ? {
            ...current,
            enrolledPlans: [
              ...(current.enrolledPlans ?? []),
              enrollPlanForm.values.planID,
            ],
          }
          : current
      );
      closeEnrollPlanModal();
      reloadStudents();
      reloadStats();
      enrollPlanForm.reset();
    } else {
      DefaultErrorNotification(r?.message || "Failed to add student to plan");
    }
  };

  const handleRemovePlanClick = (
    student: StudentProfileProps,
    event?: React.MouseEvent
  ) => {
    event?.stopPropagation();
    setSelectedStudent(student);
    setRemovePlanID("");
    openRemovePlanModal();
  };

  const handleRemoveStudentFromPlan = async () => {
    if (!selectedStudent || !removePlanID) return;
    setIsRemovingStudentFromPlan(true);
    const endpoint = Endpoints.GetRemoveStudentFromPlan(
      selectedStudent.id,
      removePlanID
    );
    const r = await PerformRequest({
      method: endpoint.method,
      route: endpoint.route,
      callback: () => setIsRemovingStudentFromPlan(false),
    });
    setIsRemovingStudentFromPlan(false);

    if (r && r.status === HttpSuccessCodesObj.Success) {
      DefaultSuccessNotification("Student removed from plan successfully");
      setSelectedStudent((current) =>
        current
          ? {
              ...current,
              enrolledPlans: (current.enrolledPlans ?? []).filter(
                (planID) => planID !== removePlanID
              ),
            }
          : current
      );
      closeRemovePlanModal();
      setRemovePlanID("");
      reloadStudents();
      reloadStats();
    } else {
      DefaultErrorNotification(r?.message || "Failed to remove student from plan");
    }
  };

  const updateForm = useForm({
    initialValues: {
      name: "",
      email: "",
    },
    validate: {
      name: (value) => {
        if (!value || value.length < 2)
          return "Name must be at least 2 characters";
        if (value.length > 50) return "Name must be less than 50 characters";
        return null;
      },
      email: (value) => {
        if (!value) return "Email is required";
        if (!/^\S+@\S+\.\S+$/.test(value)) return "Please enter a valid email";
        return null;
      },
    },
  });

  const handleUpdateStudent = async () => {
    const { hasErrors } = updateForm.validate();
    if (hasErrors || !selectedStudent) return;

    setIsUpdatingStudent(true);
    try {
      const { name, email } = updateForm.values;
      const r = await PerformRequest({
        method: Endpoints.GetUpdateStudentProfile(selectedStudent.id).method,
        route: Endpoints.GetUpdateStudentProfile(selectedStudent.id).route,
        data: { name, email },
        callback: () => {
          setIsUpdatingStudent(false);
        },
      });
      setIsUpdatingStudent(false);
      if (r && r.status === HttpSuccessCodesObj.Success) {
        DefaultSuccessNotification("Student updated successfully!");
        closeUpdateModal();
        reloadStudents(); // Reload the students list
        updateForm.reset();
      } else {
        DefaultErrorNotification(r?.message || "Failed to update student");
      }
    } catch (error) {
      DefaultErrorNotification("An error occurred while updating the student");
    } finally {
      setIsUpdatingStudent(false);
    }
  };

  const handleCancelUpdate = () => {
    closeUpdateModal();
    updateForm.reset();
    setSelectedStudent(null);
  };

  const availablePlans =
    plans?.data
      ?.filter((plan) => plan.isActive)
      .map((plan) => ({
        value: plan.id,
        label: `${plan.name} (${getCurrencySymbol("GBP")}${plan.price})`,
      })) ?? [];

  const availablePlansForSelectedStudent = availablePlans.filter(
    (plan) => !selectedStudent?.enrolledPlans?.includes(plan.value)
  );
  const enrolledPlanOptions =
    plans?.data
      ?.filter((plan) => selectedStudent?.enrolledPlans?.includes(plan.id))
      .map((plan) => ({ value: plan.id, label: plan.name })) ?? [];

  const searchForm = useForm({
    initialValues: {
      query: "",
    },
    validate: {
      query: (value) =>
        value && value.length > 2 ? null : "Minimum 3 characters required",
    },
  });
  const [isSearching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<StudentProfileProps[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [debouncedSearchQuery] = useDebouncedValue(
    searchForm.values.query,
    500
  );

  const SearchForStudents = async () => {
    const { hasErrors } = searchForm.validate();
    if (!hasErrors) {
      setSearching(true);
      const r = await PerformRequest<
        NonPaginatedResponse<StudentProfileProps[]>
      >({
        method: Endpoints.GetSearchForStudents(searchForm.values.query).method,
        route: Endpoints.GetSearchForStudents(searchForm.values.query).route,
        callback: () => {
          setSearching(false);
        },
      });
      setSearching(false);
      if (r && r.data && r.data.data) {
        setSearchResults(r.data.data);
        setShowSearchResults(true);
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
      }
    }
  };

  const filterForm = useForm({
    initialValues: {
      isEmailVerified: "" as string,
      isActive: "" as string,
      isAccountSuspended: "" as string,
    },
  });

  const [isFiltering, setFiltering] = useState(false);
  const FilterStudents = async () => {
    const { hasErrors } = filterForm.validate();
    if (!hasErrors) {
      setFiltering(true);
      const { isEmailVerified, isActive, isAccountSuspended } =
        filterForm.values;
      const data: any = {};
      if (isEmailVerified) data.isEmailVerified = isEmailVerified;
      if (isActive) data.isActive = isActive;
      if (isAccountSuspended) data.isAccountSuspended = isAccountSuspended;
      const r = await PerformRequest<
        NonPaginatedResponse<StudentProfileProps[]>
      >({
        method: Endpoints.GetFilterStudents().method,
        route: Endpoints.GetFilterStudents().route,
        data: data,
      });
      setFiltering(false);
      if (r && r.data && r.data.data) {
        setFilterResults(r.data.data);
        setShowFilterResults(true);
      } else {
        setFilterResults([]);
        setShowFilterResults(false);
      }
    }
  };

  // Filter results state
  const [filterResults, setFilterResults] = useState<StudentProfileProps[]>([]);
  const [showFilterResults, setShowFilterResults] = useState(false);

  // Auto-search when debounced query changes
  React.useEffect(() => {
    if (debouncedSearchQuery && debouncedSearchQuery.length >= 3) {
      SearchForStudents();
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  }, [debouncedSearchQuery]);

  // Auto-filter when form values change
  React.useEffect(() => {
    // Only call API if we have at least one filter active
    if (
      filterForm.values.isEmailVerified ||
      filterForm.values.isActive ||
      filterForm.values.isAccountSuspended
    ) {
      FilterStudents();
    } else {
      setFilterResults([]);
      setShowFilterResults(false);
    }
  }, [filterForm.values]);

  const clearSearch = () => {
    searchForm.reset();
    setSearchResults([]);
    setShowSearchResults(false);
  };

  // Clear all filters
  const clearFilters = () => {
    filterForm.reset();
    setFilterResults([]);
    setShowFilterResults(false);
  };

  // Filter function for students - now just returns the original list since we use server-side filtering
  const getFilteredStudents = (studentList: StudentProfileProps[]) => {
    if (!studentList) return [];
    return studentList; // No client-side filtering needed anymore
  };

  // Check if any filters are active
  const hasActiveFilters =
    filterForm.values.isEmailVerified !== "" ||
    filterForm.values.isActive !== "" ||
    filterForm.values.isAccountSuspended !== "";

  // Toggle states for showing/hiding sections - mutually exclusive
  const [showSearchBar, setShowSearchBar] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // Handle search bar toggle
  const handleSearchToggle = () => {
    if (!showSearchBar) {
      setShowSearchBar(true);
      setShowFilters(false); // Hide filters when showing search
      // Clear filter results when switching to search
      clearFilters();
    } else {
      setShowSearchBar(false);
    }
  };

  // Handle filters toggle
  const handleFiltersToggle = () => {
    if (!showFilters) {
      setShowFilters(true);
      setShowSearchBar(false); // Hide search when showing filters
      // Clear search results when switching to filters
      clearSearch();
    } else {
      setShowFilters(false);
    }
  };

  // CSV Export functionality
  const [isExporting, setIsExporting] = useState(false);

  const exportToCSV = async () => {
    setIsExporting(true);
    try {
      // Use search results if available, otherwise fetch all students
      let dataToExport: StudentProfileProps[] = [];

      if (showSearchResults && searchResults.length > 0) {
        dataToExport = searchResults;
      } else {
        // Fetch all students for export
        const response = await PerformRequest<
          PaginatedResponse<StudentProfileProps[]>
        >({
          method: Endpoints.GetStudents(1, 1000).method, // Get first 1000 students
          route: Endpoints.GetStudents(1, 1000).route,
        });

        if (response && response.data && response.data.data) {
          dataToExport = response.data.data;
        } else {
          DefaultErrorNotification("Failed to fetch student data for export");
          return;
        }
      }

      // Convert to CSV format
      const csvHeaders = [
        "ID",
        "Name",
        "Email",
        "Status",
        "Email Verified",
        "Phone Verified",
        "Login Method",
        "Enrolled Plans",
        "Account Suspended",
        "Account Deleted",
        "Active",
        "Created At",
        "Updated At",
      ];

      const csvRows = dataToExport.map((student) => [
        student.id,
        `"${student.name}"`, // Quote names in case they contain commas
        student.email,
        getStatusLabel(student),
        student.isEmailVerified ? "Yes" : "No",
        student.isPhoneVerified ? "Yes" : "No",
        student.login_method || "N/A",
        student.enrolledPlans?.length || 0,
        student.isAccountSuspended ? "Yes" : "No",
        student.isAccountDeleted ? "Yes" : "No",
        student.isActive ? "Yes" : "No",
        dayjs(student.createdAt).format("YYYY-MM-DD HH:mm:ss"),
        dayjs(student.updatedAt).format("YYYY-MM-DD HH:mm:ss"),
      ]);

      // Create CSV content
      const csvContent = [
        csvHeaders.join(","),
        ...csvRows.map((row) => row.join(",")),
      ].join("\n");

      // Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);

        // Generate filename with timestamp
        const timestamp = dayjs().format("YYYY-MM-DD_HH-mm-ss");
        const filename = showSearchResults
          ? `students_search_results_${timestamp}.csv`
          : `students_export_${timestamp}.csv`;

        link.setAttribute("download", filename);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        DefaultSuccessNotification(
          `Successfully exported ${dataToExport.length} student${dataToExport.length !== 1 ? "s" : ""
          } to CSV`
        );
      }
    } catch (error) {
      DefaultErrorNotification("An error occurred while exporting data");
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteStudent = async () => {
    if (!selectedStudent) return;

    setIsDeletingStudent(true);
    try {
      const response = await PerformRequest({
        method: Endpoints.GetDeleteStudentProfile(selectedStudent.id).method,
        route: Endpoints.GetDeleteStudentProfile(selectedStudent.id).route,
        callback: () => {
          setIsDeletingStudent(false);
        },
      });

      setIsDeletingStudent(false);
      if (response && response.status === HttpSuccessCodesObj.Success) {
        DefaultSuccessNotification("Student deleted successfully!");
        closeDeleteModal();
        reloadStudents(); // Reload the students list
        reloadStats(); // Reload stats
        setSelectedStudent(null);
      } else {
        DefaultErrorNotification(
          response?.message || "Failed to delete student"
        );
      }
    } catch (error) {
      DefaultErrorNotification("An error occurred while deleting the student");
      setIsDeletingStudent(false);
    }
  };

  const handleToggleSuspension = async () => {
    if (!selectedStudent) return;

    setIsTogglingSuspension(true);
    try {
      const response = await PerformRequest({
        method: Endpoints.GetToggleStudentSuspension(selectedStudent.id).method,
        route: Endpoints.GetToggleStudentSuspension(selectedStudent.id).route,
        callback: () => {
          setIsTogglingSuspension(false);
        },
      });

      setIsTogglingSuspension(false);
      if (response && response.status === HttpSuccessCodesObj.Success) {
        const action = selectedStudent.isAccountSuspended
          ? "unsuspended"
          : "suspended";
        DefaultSuccessNotification(`Student ${action} successfully!`);
        closeSuspensionModal();
        reloadStudents(); // Reload the students list
        reloadStats(); // Reload stats
        setSelectedStudent(null);
      } else {
        DefaultErrorNotification(
          response?.message || "Failed to update suspension status"
        );
      }
    } catch (error) {
      DefaultErrorNotification(
        "An error occurred while updating suspension status"
      );
      setIsTogglingSuspension(false);
    }
  };

  const handleCancelDelete = () => {
    closeDeleteModal();
    setSelectedStudent(null);
  };

  const handleCancelSuspension = () => {
    closeSuspensionModal();
    setSelectedStudent(null);
  };

  // Helper function to get student initials
  const getStudentInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");
  };

  // Helper function to highlight search terms
  const highlightText = (text: string, searchTerm: string) => {
    if (!searchTerm || !text) return text;

    const regex = new RegExp(
      `(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi"
    );
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <Mark key={index} color="blue">
          {part}
        </Mark>
      ) : (
        part
      )
    );
  };

  const getStatusColor = (student: StudentProfileProps) => {
    if (student.isAccountDeleted) return "gray";
    if (student.isAccountSuspended) return "red";
    if (!student.isActive) return "orange";
    return "green";
  };

  const getStatusLabel = (student: StudentProfileProps) => {
    if (student.isAccountDeleted) return "Deleted";
    if (student.isAccountSuspended) return "Suspended";
    if (!student.isActive) return "Inactive";
    return "Active";
  };

  const totalStudents = students?.totalRecords || 0;
  const activeStudents =
    students?.data?.filter(
      (s) => s.isActive && !s.isAccountDeleted && !s.isAccountSuspended
    ).length || 0;
  const verifiedStudents =
    students?.data?.filter((s) => s.isEmailVerified).length || 0;
  const suspendedStudents =
    students?.data?.filter((s) => s.isAccountSuspended).length || 0;

  const StatCard = ({ title, value, icon, color, description }: any) => (
    <Card shadow="sm" padding="xl" radius="md" withBorder>
      <Group justify="space-between">
        <div>
          <Text c="dimmed" size="sm" fw={500} tt="uppercase">
            {title}
          </Text>
          {isLoadingStats ? (
            <Skeleton height={24} width={80} my={4} />
          ) : (
            <Text fw={700} size="xl">
              <NumberFormatter value={value} thousandSeparator />
            </Text>
          )}
          {description && (
            <Text c="dimmed" size="xs" mt={4}>
              {description}
            </Text>
          )}
        </div>
        <ThemeIcon color={color} size={60} radius="md">
          {icon}
        </ThemeIcon>
      </Group>
    </Card>
  );

  return (
    <div style={{ padding: "32px", background: "#f8f9fa", minHeight: "100vh" }}>
      <Container size="xl">
        {/* Header */}
        <Group justify="space-between" align="center" mb={32}>
          <div>
            <Text size="xl" fw={700} mb={4}>
              Student Management
            </Text>
            <Text c="dimmed" size="sm">
              Manage and monitor all registered students
            </Text>
          </div>
          <Group gap={12}>
            <Group gap={8}>
              <Tooltip
                label={showSearchBar ? "Hide Search Bar" : "Show Search Bar"}
              >
                <Button
                  variant={showSearchBar ? "filled" : "outline"}
                  color="blue"
                  size="sm"
                  onClick={handleSearchToggle}
                  leftSection={<IconSearch size={16} />}
                  radius="md"
                >
                  Search
                </Button>
              </Tooltip>
              <Tooltip label={showFilters ? "Hide Filters" : "Show Filters"}>
                <Button
                  variant={showFilters ? "filled" : "outline"}
                  color="violet"
                  size="sm"
                  onClick={handleFiltersToggle}
                  leftSection={<IconFilter size={16} />}
                  radius="md"
                >
                  Filters
                </Button>
              </Tooltip>
            </Group>
            <Divider orientation="vertical" />
            <Button
              leftSection={<IconFileExport size={16} />}
              variant="outline"
              color="green"
              onClick={exportToCSV}
              loading={isExporting}
              disabled={isExporting}
            >
              {isExporting
                ? "Exporting..."
                : showSearchResults
                  ? `Export Search (${searchResults.length})`
                  : "Export to CSV"}
            </Button>
            <Button
              leftSection={<IconRefresh size={16} />}
              variant="filled"
              color="blue"
              onClick={() => {
                reloadStudents();
                reloadStats();
              }}
              loading={isLoadingStudents || isLoadingStats}
            >
              Reload
            </Button>
          </Group>
        </Group>

        {/* Stats Cards */}
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg" mb={32}>
          <StatCard
            title="Total Students"
            value={totalStudents}
            icon={<IconUsers size={30} />}
            color="blue"
            description="All registered students"
          />
          <StatCard
            title="Active Students"
            value={activeStudents}
            icon={<IconUserCheck size={30} />}
            color="green"
            description="Currently active accounts"
          />
          <StatCard
            title="Verified Students"
            value={verifiedStudents}
            icon={<IconShield size={30} />}
            color="violet"
            description="Email verified accounts"
          />
          <StatCard
            title="Suspended"
            value={suspendedStudents}
            icon={<IconUserX size={30} />}
            color="red"
            description="Suspended accounts"
          />
        </SimpleGrid>

        {/* Search Bar */}
        {showSearchBar && (
          <Card shadow="sm" padding="lg" radius="md" withBorder mb={24}>
            <Group justify="space-between" align="center" mb="md">
              <Group gap={8} align="center">
                <ThemeIcon size="sm" color="blue" variant="light" radius="md">
                  <IconSearch size={14} />
                </ThemeIcon>
                <Text fw={600} size="md" c="dark">
                  Search Students
                </Text>
              </Group>
              <Group gap={8}>
                {showSearchResults && (
                  <Button
                    size="xs"
                    variant="subtle"
                    color="gray"
                    onClick={clearSearch}
                    leftSection={<IconX size={14} />}
                  >
                    Clear Search
                  </Button>
                )}
                <Tooltip label="Hide Search Bar">
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    onClick={() => setShowSearchBar(false)}
                    size="sm"
                  >
                    <IconX size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Group>
            <TextInput
              placeholder="Search by name, email, or ID (minimum 3 characters)"
              size="md"
              leftSection={<IconSearch size={18} />}
              rightSection={
                isSearching ? (
                  <Loader size="sm" />
                ) : searchForm.values.query.length >= 3 ? (
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    onClick={clearSearch}
                    size="sm"
                  >
                    <IconX size={16} />
                  </ActionIcon>
                ) : null
              }
              {...searchForm.getInputProps("query")}
              radius="md"
            />
            {showSearchResults && (
              <>
                <Text size="sm" c="dimmed" mt="md" mb="sm">
                  Found {searchResults.length} student
                  {searchResults.length !== 1 ? "s" : ""}
                </Text>
                {searchResults.length > 0 ? (
                  <Grid gutter="md">
                    {searchResults.map((student) => (
                      <Grid.Col
                        key={student.id}
                        span={{ base: 12, sm: 6, lg: 4, xl: 3 }}
                      >
                        <Card
                          shadow="xs"
                          padding="md"
                          radius="md"
                          withBorder
                          style={{
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            background: "#f8f9fa",
                          }}
                          onClick={() => handleStudentClick(student)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform =
                              "translateY(-2px)";
                            e.currentTarget.style.boxShadow =
                              "0 4px 15px rgba(0,0,0,0.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "";
                          }}
                        >
                          <Stack gap="sm">
                            <Group justify="space-between" align="flex-start">
                              <Avatar
                                size={40}
                                radius="xl"
                                style={{ border: "2px solid #e9ecef" }}
                              >
                                {getStudentInitials(student.name)}
                              </Avatar>
                              <Badge
                                color={getStatusColor(student)}
                                variant="light"
                                size="xs"
                                radius="xl"
                              >
                                {getStatusLabel(student)}
                              </Badge>
                            </Group>

                            <div>
                              <Text fw={600} size="sm" lineClamp={1}>
                                {highlightText(
                                  student.name,
                                  searchForm.values.query
                                )}
                              </Text>
                              <Text c="dimmed" size="xs" lineClamp={1}>
                                {highlightText(
                                  student.email,
                                  searchForm.values.query
                                )}
                              </Text>
                            </div>

                            <Group gap={4} justify="center">
                              {student.isEmailVerified && (
                                <Tooltip label="Email Verified">
                                  <ThemeIcon
                                    size="xs"
                                    color="green"
                                    variant="light"
                                    radius="xl"
                                  >
                                    <IconCheck size={10} />
                                  </ThemeIcon>
                                </Tooltip>
                              )}
                              {student.isPhoneVerified && (
                                <Tooltip label="Phone Verified">
                                  <ThemeIcon
                                    size="xs"
                                    color="blue"
                                    variant="light"
                                    radius="xl"
                                  >
                                    <IconPhone size={10} />
                                  </ThemeIcon>
                                </Tooltip>
                              )}
                              <Tooltip
                                label={`${student.enrolledPlans?.length || 0
                                  } Plans`}
                              >
                                <ThemeIcon
                                  size="xs"
                                  color="violet"
                                  variant="light"
                                  radius="xl"
                                >
                                  <IconBook size={10} />
                                </ThemeIcon>
                              </Tooltip>
                            </Group>

                            <Group justify="space-between" align="center">
                              <Text size="xs" c="dimmed">
                                {dayjs(student.createdAt).format("DD MMM YY")}
                              </Text>
                              <Group gap={2}>
                                <ActionIcon
                                  variant="subtle"
                                  size="xs"
                                  color="blue"
                                >
                                  <IconEye size={12} />
                                </ActionIcon>
                                <ActionIcon
                                  variant="subtle"
                                  size="xs"
                                  color="yellow"
                                  onClick={(e) => handleEditClick(student, e)}
                                >
                                  <IconEdit size={12} />
                                </ActionIcon>
                                <ActionIcon
                                  variant="subtle"
                                  size="xs"
                                  color="red"
                                  onClick={(e) => handleDeleteClick(student, e)}
                                >
                                  <IconTrash size={12} />
                                </ActionIcon>
                              </Group>
                            </Group>
                          </Stack>
                        </Card>
                      </Grid.Col>
                    ))}
                  </Grid>
                ) : (
                  <Paper
                    style={{
                      textAlign: "center",
                      padding: "24px",
                      background: "#f8f9fa",
                      border: "1px solid #e9ecef",
                      borderRadius: 8,
                    }}
                  >
                    <IconSearch
                      size={32}
                      style={{ marginBottom: 8, opacity: 0.5 }}
                    />
                    <Text size="sm" fw={500} mb={4}>
                      No students found
                    </Text>
                    <Text size="xs" c="dimmed">
                      No students match your search criteria
                    </Text>
                  </Paper>
                )}
              </>
            )}
          </Card>
        )}

        {/* Filter Row */}
        {showFilters && (
          <Card shadow="sm" padding="xl" radius="md" withBorder mb={24}>
            <Stack gap="lg">
              <Group justify="space-between" align="center">
                <Group gap={8} align="center">
                  <ThemeIcon
                    size="sm"
                    color="violet"
                    variant="light"
                    radius="md"
                  >
                    <IconFilter size={14} />
                  </ThemeIcon>
                  <Text fw={600} size="md" c="dark">
                    Filter Students
                  </Text>
                </Group>
                <Group gap={8}>
                  {hasActiveFilters && (
                    <Button
                      size="sm"
                      variant="light"
                      color="gray"
                      onClick={clearFilters}
                      leftSection={<IconX size={14} />}
                      radius="md"
                      loading={isFiltering}
                    >
                      Clear All
                    </Button>
                  )}
                  <Tooltip label="Hide Filters">
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={() => setShowFilters(false)}
                      size="sm"
                    >
                      <IconX size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Group>

              <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="xl">
                {/* Email Verification Filter */}
                <Stack gap="sm">
                  <Group gap={8} align="center">
                    <ThemeIcon
                      size="xs"
                      color="green"
                      variant="light"
                      radius="sm"
                    >
                      <IconMail size={10} />
                    </ThemeIcon>
                    <Text size="sm" fw={500} c="dimmed">
                      Email Verification
                    </Text>
                  </Group>
                  <Select
                    placeholder="Select verification status"
                    data={[
                      { value: "true", label: "✓ Verified" },
                      { value: "false", label: "✗ Not Verified" },
                    ]}
                    value={filterForm.values.isEmailVerified}
                    onChange={(value) =>
                      filterForm.setFieldValue("isEmailVerified", value || "")
                    }
                    clearable
                    radius="md"
                    size="sm"
                  />
                </Stack>

                {/* Account Status Filter */}
                <Stack gap="sm">
                  <Group gap={8} align="center">
                    <ThemeIcon
                      size="xs"
                      color="blue"
                      variant="light"
                      radius="sm"
                    >
                      <IconUser size={10} />
                    </ThemeIcon>
                    <Text size="sm" fw={500} c="dimmed">
                      Account Status
                    </Text>
                  </Group>
                  <Select
                    placeholder="Select account status"
                    data={[
                      { value: "true", label: "✓ Active" },
                      { value: "false", label: "⏸ Inactive" },
                    ]}
                    value={filterForm.values.isActive}
                    onChange={(value) =>
                      filterForm.setFieldValue("isActive", value || "")
                    }
                    clearable
                    radius="md"
                    size="sm"
                  />
                </Stack>

                {/* Suspension Filter */}
                <Stack gap="sm">
                  <Group gap={8} align="center">
                    <ThemeIcon
                      size="xs"
                      color="red"
                      variant="light"
                      radius="sm"
                    >
                      <IconShield size={10} />
                    </ThemeIcon>
                    <Text size="sm" fw={500} c="dimmed">
                      Account Suspension
                    </Text>
                  </Group>
                  <Select
                    placeholder="Select suspension status"
                    data={[
                      { value: "false", label: "🛡 Not Suspended" },
                      { value: "true", label: "🚫 Suspended" },
                    ]}
                    value={filterForm.values.isAccountSuspended}
                    onChange={(value) =>
                      filterForm.setFieldValue(
                        "isAccountSuspended",
                        value || ""
                      )
                    }
                    clearable
                    radius="md"
                    size="sm"
                  />
                </Stack>
              </SimpleGrid>

              {hasActiveFilters && (
                <Paper
                  style={{
                    background:
                      "linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)",
                    padding: "12px 16px",
                    borderRadius: 8,
                    border: "1px solid #e0e7ff",
                  }}
                >
                  <Group gap={8} align="center">
                    <ThemeIcon
                      size="xs"
                      color="blue"
                      variant="light"
                      radius="sm"
                    >
                      <IconInfoCircle size={12} />
                    </ThemeIcon>
                    <Text size="xs" c="blue" fw={500}>
                      Showing students matching the selected filters
                    </Text>
                  </Group>
                </Paper>
              )}
            </Stack>
          </Card>
        )}

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(value) => setActiveTab(value || "all")}
          mb={24}
        >
          <Tabs.List grow>
            <Tabs.Tab value="all" leftSection={<IconUsers size={16} />}>
              All Students ({totalStudents})
            </Tabs.Tab>
            <Tabs.Tab
              value="analytics"
              leftSection={<IconChartBar size={16} />}
            >
              Analytics
            </Tabs.Tab>
          </Tabs.List>

          {/* All Students Tab */}
          <Tabs.Panel value="all" pt="lg">
            {isLoadingStudents ? (
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing="lg">
                {Array.from({ length: 8 }).map((_, index) => (
                  <Skeleton key={index} height={200} radius="md" />
                ))}
              </SimpleGrid>
            ) : showFilterResults ? (
              // Show filter results
              <>
                {filterResults.length > 0 ? (
                  <Grid gutter="lg">
                    {filterResults.map((student) => (
                      <Grid.Col
                        key={student.id}
                        span={{ base: 12, sm: 6, lg: 4, xl: 3 }}
                      >
                        <Card
                          shadow="sm"
                          padding="lg"
                          radius="md"
                          withBorder
                          style={{
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            height: "100%",
                          }}
                          onClick={() => handleStudentClick(student)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform =
                              "translateY(-4px)";
                            e.currentTarget.style.boxShadow =
                              "0 8px 25px rgba(0,0,0,0.12)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "";
                          }}
                        >
                          <Stack gap="md">
                            <Group justify="space-between" align="flex-start">
                              <Avatar
                                size={60}
                                radius="xl"
                                style={{ border: "3px solid #e9ecef" }}
                              >
                                {getStudentInitials(student.name)}
                              </Avatar>
                              <Badge
                                color={getStatusColor(student)}
                                variant="light"
                                size="sm"
                                radius="xl"
                              >
                                {getStatusLabel(student)}
                              </Badge>
                            </Group>

                            <div>
                              <Text fw={600} size="sm" lineClamp={1}>
                                {student.name}
                              </Text>
                              <Text c="dimmed" size="xs" lineClamp={1}>
                                {student.email}
                              </Text>
                            </div>

                            <Group gap={8} justify="center">
                              {student.isEmailVerified && (
                                <Tooltip label="Email Verified">
                                  <ThemeIcon
                                    size="sm"
                                    color="green"
                                    variant="light"
                                    radius="xl"
                                  >
                                    <IconCheck size={12} />
                                  </ThemeIcon>
                                </Tooltip>
                              )}
                              {student.isPhoneVerified && (
                                <Tooltip label="Phone Verified">
                                  <ThemeIcon
                                    size="sm"
                                    color="blue"
                                    variant="light"
                                    radius="xl"
                                  >
                                    <IconPhone size={12} />
                                  </ThemeIcon>
                                </Tooltip>
                              )}
                              <Tooltip
                                label={`${student.enrolledPlans?.length || 0
                                  } Plans Enrolled`}
                              >
                                <ThemeIcon
                                  size="sm"
                                  color="violet"
                                  variant="light"
                                  radius="xl"
                                >
                                  <IconBook size={12} />
                                </ThemeIcon>
                              </Tooltip>
                            </Group>

                            <Divider />

                            <Group justify="space-between" align="center">
                              <Text size="xs" c="dimmed">
                                Member since{" "}
                                {dayjs(student.createdAt).format("DD MMM YYYY")}
                              </Text>
                              <Group gap={4}>
                                <ActionIcon
                                  variant="subtle"
                                  size="sm"
                                  color="blue"
                                >
                                  <IconEye size={14} />
                                </ActionIcon>
                                <ActionIcon
                                  variant="subtle"
                                  size="sm"
                                  color="yellow"
                                  onClick={(e) => handleEditClick(student, e)}
                                >
                                  <IconEdit size={14} />
                                </ActionIcon>
                                <ActionIcon
                                  variant="subtle"
                                  size="sm"
                                  color="red"
                                  onClick={(e) => handleDeleteClick(student, e)}
                                >
                                  <IconTrash size={14} />
                                </ActionIcon>
                              </Group>
                            </Group>
                          </Stack>
                        </Card>
                      </Grid.Col>
                    ))}
                  </Grid>
                ) : (
                  <Paper
                    style={{
                      textAlign: "center",
                      padding: "48px 24px",
                      background: "#f8f9fa",
                      border: "1px solid #e9ecef",
                      borderRadius: 12,
                    }}
                  >
                    <IconFilter
                      size={64}
                      style={{ marginBottom: 16, opacity: 0.5 }}
                    />
                    <Text size="lg" fw={500} mb={8}>
                      No students match the selected filters
                    </Text>
                    <Text size="sm" c="dimmed" mb={16}>
                      Try adjusting your filter criteria or clear all filters.
                    </Text>
                    <Button
                      variant="outline"
                      onClick={clearFilters}
                      leftSection={<IconX size={16} />}
                    >
                      Clear All Filters
                    </Button>
                  </Paper>
                )}
              </>
            ) : students?.data && students.data.length > 0 ? (
              // Show regular students
              <>
                <Grid gutter="lg">
                  {getFilteredStudents(students.data).map((student) => (
                    <Grid.Col
                      key={student.id}
                      span={{ base: 12, sm: 6, lg: 4, xl: 3 }}
                    >
                      <Card
                        shadow="sm"
                        padding="lg"
                        radius="md"
                        withBorder
                        style={{
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          height: "100%",
                        }}
                        onClick={() => handleStudentClick(student)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-4px)";
                          e.currentTarget.style.boxShadow =
                            "0 8px 25px rgba(0,0,0,0.12)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "";
                        }}
                      >
                        <Stack gap="md">
                          <Group justify="space-between" align="flex-start">
                            <Avatar
                              size={60}
                              radius="xl"
                              style={{ border: "3px solid #e9ecef" }}
                            >
                              {getStudentInitials(student.name)}
                            </Avatar>
                            <Badge
                              color={getStatusColor(student)}
                              variant="light"
                              size="sm"
                              radius="xl"
                            >
                              {getStatusLabel(student)}
                            </Badge>
                          </Group>

                          <div>
                            <Text fw={600} size="sm" lineClamp={1}>
                              {student.name}
                            </Text>
                            <Text c="dimmed" size="xs" lineClamp={1}>
                              {student.email}
                            </Text>
                          </div>

                          <Group gap={8} justify="center">
                            {student.isEmailVerified && (
                              <Tooltip label="Email Verified">
                                <ThemeIcon
                                  size="sm"
                                  color="green"
                                  variant="light"
                                  radius="xl"
                                >
                                  <IconCheck size={12} />
                                </ThemeIcon>
                              </Tooltip>
                            )}
                            {student.isPhoneVerified && (
                              <Tooltip label="Phone Verified">
                                <ThemeIcon
                                  size="sm"
                                  color="blue"
                                  variant="light"
                                  radius="xl"
                                >
                                  <IconPhone size={12} />
                                </ThemeIcon>
                              </Tooltip>
                            )}
                            <Tooltip
                              label={`${student.enrolledPlans?.length || 0
                                } Plans Enrolled`}
                            >
                              <ThemeIcon
                                size="sm"
                                color="violet"
                                variant="light"
                                radius="xl"
                              >
                                <IconBook size={12} />
                              </ThemeIcon>
                            </Tooltip>
                          </Group>

                          <Divider />

                          <Group justify="space-between" align="center">
                            <Text size="xs" c="dimmed">
                              Member since{" "}
                              {dayjs(student.createdAt).format("DD MMM YYYY")}
                            </Text>
                            <Group gap={8}>
                              <Tooltip label="View Student">
                                <ActionIcon
                                  variant="light"
                                  size="lg"
                                  radius="md"
                                  color="blue"
                                  style={{ boxShadow: "0 4px 12px rgba(37, 99, 235, 0.18)" }}
                                >
                                  <IconEye size={18} />
                                </ActionIcon>
                              </Tooltip>
                              <Tooltip label="Edit Student">
                                <ActionIcon
                                  variant="light"
                                  size="lg"
                                  radius="md"
                                  color="yellow"
                                  onClick={(e) => handleEditClick(student, e)}
                                  style={{ boxShadow: "0 4px 12px rgba(234, 179, 8, 0.2)" }}
                                >
                                  <IconEdit size={18} />
                                </ActionIcon>
                              </Tooltip>
                              <Tooltip label="Add To Plan">
                                <ActionIcon
                                  variant="light"
                                  size="lg"
                                  radius="md"
                                  color="green"
                                  onClick={(e) => handleEnrollPlanClick(student, e)}
                                  style={{ boxShadow: "0 4px 12px rgba(22, 163, 74, 0.18)" }}
                                >
                                  <IconPlus size={18} />
                                </ActionIcon>
                              </Tooltip>
                              {student.enrolledPlans?.length > 0 && (
                                <Tooltip label="Remove From Plan">
                                  <ActionIcon
                                    variant="light"
                                    size="lg"
                                    radius="md"
                                    color="red"
                                    onClick={(e) => handleRemovePlanClick(student, e)}
                                  >
                                    <IconUserX size={18} />
                                  </ActionIcon>
                                </Tooltip>
                              )}
                              <Tooltip label="Login As Student">
                                <ActionIcon
                                  variant="filled"
                                  size="lg"
                                  radius="md"
                                  color="grape"
                                  onClick={(e) =>
                                    handleSimulateStudentLogin(student, e)
                                  }
                                  style={{
                                    background:
                                      "linear-gradient(135deg, #7c3aed 0%, #c026d3 100%)",
                                    boxShadow: "0 8px 18px rgba(124, 58, 237, 0.32)",
                                  }}
                                >
                                  <IconLogin2 size={18} />
                                </ActionIcon>
                              </Tooltip>
                              <Tooltip label="Delete Student">
                                <ActionIcon
                                  variant="light"
                                  size="lg"
                                  radius="md"
                                  color="red"
                                  onClick={(e) => handleDeleteClick(student, e)}
                                  style={{ boxShadow: "0 4px 12px rgba(220, 38, 38, 0.18)" }}
                                >
                                  <IconTrash size={18} />
                                </ActionIcon>
                              </Tooltip>
                            </Group>
                          </Group>
                        </Stack>
                      </Card>
                    </Grid.Col>
                  ))}
                </Grid>

                {/* Pagination - only show when no filters are active */}
                {totalStudents > pagination.limit && !hasActiveFilters && (
                  <Group justify="center" mt={32}>
                    <Pagination
                      total={Math.ceil(totalStudents / pagination.limit)}
                      value={pagination.page}
                      onChange={(page) =>
                        setPagination({ ...pagination, page })
                      }
                      size="md"
                      radius="xl"
                      withEdges
                    />
                  </Group>
                )}
              </>
            ) : (
              <Paper
                style={{
                  textAlign: "center",
                  padding: "48px 24px",
                  background: "#f8f9fa",
                  border: "1px solid #e9ecef",
                  borderRadius: 12,
                }}
              >
                <IconUsers
                  size={64}
                  style={{ marginBottom: 16, opacity: 0.5 }}
                />
                <Text size="lg" fw={500} mb={8}>
                  No students found
                </Text>
                <Text size="sm" c="dimmed">
                  There are no students in the system yet.
                </Text>
              </Paper>
            )}
          </Tabs.Panel>

          {/* Analytics Tab */}
          <Tabs.Panel value="analytics" pt="lg">
            <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="xl">
              <Card shadow="sm" padding="xl" radius="md" withBorder>
                <Text fw={600} mb="lg">
                  Student Verification Status
                </Text>
                <RingProgress
                  size={200}
                  thickness={20}
                  sections={[
                    {
                      value: (verifiedStudents / totalStudents) * 100,
                      color: "green",
                    },
                    {
                      value:
                        ((totalStudents - verifiedStudents) / totalStudents) *
                        100,
                      color: "gray",
                    },
                  ]}
                  label={
                    <Text ta="center" fw={700} size="xl">
                      {Math.round((verifiedStudents / totalStudents) * 100)}%
                    </Text>
                  }
                />
                <Text ta="center" c="dimmed" size="sm" mt="md">
                  {verifiedStudents} of {totalStudents} students verified
                </Text>
              </Card>

              <Card shadow="sm" padding="xl" radius="md" withBorder>
                <Text fw={600} mb="lg">
                  Account Status Distribution
                </Text>
                <Stack gap="md">
                  <Group justify="space-between">
                    <Group gap={8}>
                      <ThemeIcon size="sm" color="green" variant="light">
                        <IconCheck size={12} />
                      </ThemeIcon>
                      <Text size="sm">Active</Text>
                    </Group>
                    <Progress
                      value={(activeStudents / totalStudents) * 100}
                      size="sm"
                      style={{ flex: 1, marginLeft: 16 }}
                    />
                    <Text size="sm" fw={500}>
                      {activeStudents}
                    </Text>
                  </Group>
                  <Group justify="space-between">
                    <Group gap={8}>
                      <ThemeIcon size="sm" color="orange" variant="light">
                        <IconClock size={12} />
                      </ThemeIcon>
                      <Text size="sm">Inactive</Text>
                    </Group>
                    <Progress
                      value={
                        ((totalStudents - activeStudents - suspendedStudents) /
                          totalStudents) *
                        100
                      }
                      color="orange"
                      size="sm"
                      style={{ flex: 1, marginLeft: 16 }}
                    />
                    <Text size="sm" fw={500}>
                      {totalStudents - activeStudents - suspendedStudents}
                    </Text>
                  </Group>
                  <Group justify="space-between">
                    <Group gap={8}>
                      <ThemeIcon size="sm" color="red" variant="light">
                        <IconX size={12} />
                      </ThemeIcon>
                      <Text size="sm">Suspended</Text>
                    </Group>
                    <Progress
                      value={(suspendedStudents / totalStudents) * 100}
                      color="red"
                      size="sm"
                      style={{ flex: 1, marginLeft: 16 }}
                    />
                    <Text size="sm" fw={500}>
                      {suspendedStudents}
                    </Text>
                  </Group>
                </Stack>
              </Card>
            </SimpleGrid>
          </Tabs.Panel>
        </Tabs>

        {/* Student Detail Modal */}
        <Modal
          opened={isDetailModalOpened}
          onClose={closeDetailModal}
          title={
            <Group gap={12}>
              <Avatar size={40} radius="xl">
                {selectedStudent
                  ? getStudentInitials(selectedStudent.name)
                  : ""}
              </Avatar>
              <div>
                <Text fw={600}>{selectedStudent?.name}</Text>
                <Text size="sm" c="dimmed">
                  {selectedStudent?.email}
                </Text>
              </div>
            </Group>
          }
          size="lg"
          centered
          radius={12}
        >
          {selectedStudent && (
            <Stack gap="lg">
              <Group justify="space-between">
                <Badge
                  color={getStatusColor(selectedStudent)}
                  variant="light"
                  size="lg"
                >
                  {getStatusLabel(selectedStudent)}
                </Badge>
                <Group gap={8}>
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => {
                      closeDetailModal();
                      handleEditClick(selectedStudent!);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="xs"
                    color={
                      selectedStudent.isAccountSuspended ? "green" : "orange"
                    }
                    variant="outline"
                    onClick={() => {
                      closeDetailModal();
                      handleSuspensionClick(selectedStudent!);
                    }}
                  >
                    {selectedStudent.isAccountSuspended
                      ? "Unsuspend"
                      : "Suspend"}
                  </Button>
                  <Button
                    size="xs"
                    color="green"
                    variant="outline"
                    onClick={() => {
                      closeDetailModal();
                      handleEnrollPlanClick(selectedStudent!);
                    }}
                  >
                    Add To Plan
                  </Button>
                  <Button
                    size="xs"
                    color="red"
                    variant="outline"
                    onClick={() => {
                      closeDetailModal();
                      handleDeleteClick(selectedStudent!);
                    }}
                  >
                    Delete
                  </Button>
                </Group>
              </Group>

              <Accordion defaultValue="basic-info">
                <Accordion.Item value="basic-info">
                  <Accordion.Control>Basic Information</Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="sm">
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Email Verified
                        </Text>
                        <Badge
                          color={
                            selectedStudent.isEmailVerified ? "green" : "red"
                          }
                          size="sm"
                        >
                          {selectedStudent.isEmailVerified ? "Yes" : "No"}
                        </Badge>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Phone Verified
                        </Text>
                        <Badge
                          color={
                            selectedStudent.isPhoneVerified ? "green" : "red"
                          }
                          size="sm"
                        >
                          {selectedStudent.isPhoneVerified ? "Yes" : "No"}
                        </Badge>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Login Method
                        </Text>
                        <Code>{selectedStudent.login_method}</Code>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Enrolled Plans
                        </Text>
                        <Text size="sm" fw={500}>
                          {selectedStudent.enrolledPlans?.length || 0}
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Link to={`/d/student/${selectedStudent.id}`}>
                          <Text size="sm" c={Colors.BluePrimary}>
                            View Full Profile
                          </Text>
                        </Link>
                      </Group>
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
            </Stack>
          )}
        </Modal>

        {/* Update Student Modal */}
        <Modal
          opened={isEnrollPlanModalOpened}
          onClose={closeEnrollPlanModal}
          size="sm"
          centered
          radius={12}
          title="Add Student To Plan"
        >
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              {selectedStudent
                ? `Grant ${selectedStudent.name} access to an active plan for the plan's configured duration.`
                : "Grant this student access to an active plan for the plan's configured duration."}
            </Text>
            <Select
              label="Plan"
              placeholder="Select a plan"
              data={availablePlansForSelectedStudent}
              searchable
              nothingFoundMessage="No available plans"
              {...enrollPlanForm.getInputProps("planID")}
            />
            {selectedStudent &&
              availablePlansForSelectedStudent.length === 0 && (
                <Text size="sm" c="dimmed">
                  This student is already enrolled in all active plans.
                </Text>
              )}
            <Group justify="space-between">
              <Button variant="outline" onClick={closeEnrollPlanModal}>
                Cancel
              </Button>
              <Button
                color="green"
                onClick={handleEnrollStudentInPlan}
                loading={isEnrollingStudentInPlan}
                disabled={availablePlansForSelectedStudent.length === 0}
              >
                Add To Plan
              </Button>
            </Group>
          </Stack>
        </Modal>

        <Modal
          opened={isRemovePlanModalOpened}
          onClose={closeRemovePlanModal}
          size="sm"
          centered
          radius={12}
          title="Remove Student From Plan"
        >
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              {selectedStudent
                ? `Revoke ${selectedStudent.name}'s access to a plan.`
                : "Revoke this student's access to a plan."}
            </Text>
            <Select
              label="Plan"
              placeholder="Select an enrolled plan"
              data={enrolledPlanOptions}
              value={removePlanID}
              onChange={(value) => setRemovePlanID(value ?? "")}
              searchable
              nothingFoundMessage="No enrolled plans"
            />
            <Group justify="space-between">
              <Button variant="outline" onClick={closeRemovePlanModal}>
                Cancel
              </Button>
              <Button
                color="red"
                onClick={handleRemoveStudentFromPlan}
                loading={isRemovingStudentFromPlan}
                disabled={!removePlanID}
              >
                Remove From Plan
              </Button>
            </Group>
          </Stack>
        </Modal>

        <Modal
          opened={isUpdateModalOpened}
          onClose={closeUpdateModal}
          size="sm"
          withCloseButton={false}
          centered
          radius={12}
        >
          <Stack gap="lg">
            <Text fw={600} size="lg">
              Update Student Details
            </Text>
            <form onSubmit={updateForm.onSubmit(handleUpdateStudent)}>
              <Stack gap="md">
                <TextInput
                  label="Name"
                  placeholder="Enter new name"
                  {...updateForm.getInputProps("name")}
                  error={updateForm.errors.name}
                />
                <TextInput
                  label="Email"
                  placeholder="Enter new email"
                  {...updateForm.getInputProps("email")}
                  error={updateForm.errors.email}
                />
                <div className="flex-row align-center justify-between width-100">
                  <Button
                    w="48%"
                    variant="outline"
                    onClick={handleCancelUpdate}
                  >
                    Cancel
                  </Button>
                  <Button
                    w="48%"
                    type="submit"
                    loading={isUpdatingStudent}
                    disabled={isUpdatingStudent}
                  >
                    {isUpdatingStudent ? "Updating..." : "Update Student"}
                  </Button>
                </div>
              </Stack>
            </form>
          </Stack>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          opened={isDeleteModalOpened}
          onClose={closeDeleteModal}
          title={
            <Group align="center" gap={8}>
              <IconAlertTriangle size={20} color="#fa5252" />
              <Text fw={600} size="lg" c="red">
                Delete Student
              </Text>
            </Group>
          }
          centered
          radius={12}
          overlayProps={{
            backgroundOpacity: 0.55,
            blur: 3,
          }}
        >
          <Stack gap="lg">
            <div>
              <Text size="sm" c="dimmed" mb={12}>
                Are you sure you want to permanently delete this student? This
                action cannot be undone.
              </Text>

              {selectedStudent && (
                <Paper
                  style={{
                    background: "#fff5f5",
                    padding: 16,
                    borderRadius: 8,
                    border: "1px solid #fec2c2",
                  }}
                >
                  <Group align="center" gap={8} mb={8}>
                    <Avatar size={32} radius="xl">
                      {getStudentInitials(selectedStudent.name)}
                    </Avatar>
                    <div>
                      <Text fw={500} size="sm" c="red">
                        {selectedStudent.name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {selectedStudent.email}
                      </Text>
                    </div>
                  </Group>
                  <Text size="xs" c="red" fw={500}>
                    ⚠️ All student data, enrollments, and progress will be
                    permanently deleted
                  </Text>
                </Paper>
              )}
            </div>

            <Group justify="flex-end" gap={12}>
              <Button
                variant="outline"
                color="gray"
                onClick={handleCancelDelete}
                disabled={isDeletingStudent}
              >
                Cancel
              </Button>
              <Button
                color="red"
                onClick={handleDeleteStudent}
                loading={isDeletingStudent}
                leftSection={<IconTrash size={16} />}
              >
                {isDeletingStudent ? "Deleting..." : "Delete Student"}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Suspension Confirmation Modal */}
        <Modal
          opened={isSuspensionModalOpened}
          onClose={closeSuspensionModal}
          title={
            <Group align="center" gap={8}>
              <IconShield
                size={20}
                color={
                  selectedStudent?.isAccountSuspended ? "#40c057" : "#fd7e14"
                }
              />
              <Text fw={600} size="lg">
                {selectedStudent?.isAccountSuspended ? "Unsuspend" : "Suspend"}{" "}
                Student
              </Text>
            </Group>
          }
          centered
          radius={12}
          overlayProps={{
            backgroundOpacity: 0.55,
            blur: 3,
          }}
        >
          <Stack gap="lg">
            <div>
              <Text size="sm" c="dimmed" mb={12}>
                {selectedStudent?.isAccountSuspended
                  ? "This will restore the student's access to all courses and content."
                  : "This will temporarily block the student's access to all courses and content."}
              </Text>

              {selectedStudent && (
                <Paper
                  style={{
                    background: selectedStudent.isAccountSuspended
                      ? "#f3f9f0"
                      : "#fff4e6",
                    padding: 16,
                    borderRadius: 8,
                    border: selectedStudent.isAccountSuspended
                      ? "1px solid #c3fac0"
                      : "1px solid #ffd43b",
                  }}
                >
                  <Group align="center" gap={8} mb={8}>
                    <Avatar size={32} radius="xl">
                      {getStudentInitials(selectedStudent.name)}
                    </Avatar>
                    <div>
                      <Text fw={500} size="sm">
                        {selectedStudent.name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {selectedStudent.email}
                      </Text>
                    </div>
                    <Badge
                      color={getStatusColor(selectedStudent)}
                      variant="light"
                      size="sm"
                    >
                      {getStatusLabel(selectedStudent)}
                    </Badge>
                  </Group>
                  <Text
                    size="xs"
                    fw={500}
                    c={selectedStudent.isAccountSuspended ? "green" : "orange"}
                  >
                    {selectedStudent.isAccountSuspended
                      ? "✅ Student will regain full access"
                      : "⚠️ Student will lose access to all content"}
                  </Text>
                </Paper>
              )}
            </div>

            <Group justify="flex-end" gap={12}>
              <Button
                variant="outline"
                color="gray"
                onClick={handleCancelSuspension}
                disabled={isTogglingSuspension}
              >
                Cancel
              </Button>
              <Button
                color={selectedStudent?.isAccountSuspended ? "green" : "orange"}
                onClick={handleToggleSuspension}
                loading={isTogglingSuspension}
                leftSection={
                  selectedStudent?.isAccountSuspended ? (
                    <IconUserCheck size={16} />
                  ) : (
                    <IconUserX size={16} />
                  )
                }
              >
                {isTogglingSuspension
                  ? "Processing..."
                  : selectedStudent?.isAccountSuspended
                    ? "Unsuspend Student"
                    : "Suspend Student"}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Container>
    </div>
  );
};

export default Students;
